import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Label } from '@/src/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select'
import { Spinner } from '@/src/components/ui/spinner'
import { me } from '@/src/api/user'
import { getOperationType } from '@/src/api/operation-type'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { OperationTypeUpsertForm } from '@/src/components/manage/forms/OperationTypeUpsertForm'
import { languages } from '@/i18next.config'

const LOCALES = [...new Set(languages.map((l) => l.split('-')[0]))]

export const Route = createFileRoute('/_manage/manage/organization/operation-types_/$typeId/edit')({
  component: OrgOperationTypeEditPage,
})

function OrgOperationTypeEditPage() {
  const navigate = useNavigate()
  const { typeId } = useParams({ from: '/_manage/manage/organization/operation-types_/$typeId/edit' })
  const [locale, setLocale] = useState<string>(LOCALES[0] || 'en')

  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const orgId = user?.organization?.id

  const { data: operationType, isLoading: isTypeLoading } = useQuery({
    queryKey: ['operationType', typeId],
    queryFn: () => getOperationType({ data: { id: typeId } }),
    enabled: !!typeId,
  })

  useEffect(() => {
    if (!isUserLoading && !isTypeLoading) {
      if (!canManageOrg || !orgId) {
        navigate({ to: '/not-permitted' })
        return
      }

      if (operationType && operationType.organizationId !== orgId) {
        navigate({ to: '/manage/organization/operation-types' })
      }
    }
  }, [
    isUserLoading,
    isTypeLoading,
    canManageOrg,
    orgId,
    operationType,
    navigate,
  ])

  if (isUserLoading || isTypeLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !orgId) return null

  if (!operationType || operationType.organizationId !== orgId) return null

  const handleSuccess = () => {
    navigate({ to: '/manage/organization/operation-types' })
  }

  const handleCancel = () => {
    navigate({ to: '/manage/organization/operation-types' })
  }

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            Edit Operation Type: {operationType.labels?.en || operationType.name}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
                  <div className="flex items-center gap-2 h-9">
                    <Label className="text-sm leading-none whitespace-nowrap pb-0">Language</Label>
                    <Select value={locale} onValueChange={(value) => setLocale(value || LOCALES[0] || 'en')}>
                      <SelectTrigger className="w-16 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCALES.map((entry) => (
                          <SelectItem key={entry} value={entry}>
                            {entry.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto">
          <OperationTypeUpsertForm
            operationType={operationType}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
            locale={locale}
            onLocaleChange={setLocale}
          />
        </div>
      </div>
    </div>
  )
}
